/** @fileoverview Lists app datasource bindings for each connected system. */

import { useState, type CSSProperties } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { cn } from 'cn';
import { getAppSystemBindings } from '../../lib/appSystems';
import { getPlatformSystemURL } from '../../services/platformAuth';
import type { ConnectedSystem } from '../../services/systemsApi';
import type { FabrikAppDefinition } from '../../types/apps';
import {
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '../ui/sidebar';
import { getConnectedSystemLabel, getConnectedSystemLogoUrl } from '../ConnectedSystemDetails';
import { Badge } from '../ui/badge';
import { ScrollArea, ScrollAreaContent, ScrollAreaViewport, ScrollBar } from '../ui/scroll-area';
import AppearanceSettings from './AppearanceSettings';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

type Props = {
  tenantId: string;
  tenantName: string;
  appId: string;
  appName: string;
  appSlug: string;
  isAppPublished: boolean;
  onRenameApp: (name: string, preserveOldUrl: boolean) => Promise<void>;
  definition?: FabrikAppDefinition;
  onDefinitionChange: (definition: FabrikAppDefinition) => void;
  version: 'draft' | 'published';
  systems: ConnectedSystem[];
  onSystemChange: (sourceSystemId: string, targetSystemId: string) => Promise<void>;
};

const sections = [
  { label: 'Data and connectors' },
  { label: 'Appearance' },
  { label: 'Actions and process' },
  { label: 'Access', expandable: true },
  { label: 'Deployments' },
  { label: 'Usage', expandable: true },
  { label: 'Audit' },
  { label: 'History' },
];

export default function DataConnectionsSettings({
  tenantId,
  tenantName,
  appId,
  appName,
  appSlug,
  isAppPublished,
  onRenameApp,
  definition,
  onDefinitionChange,
  version,
  systems,
  onSystemChange,
}: Props) {
  const [savingSystemId, setSavingSystemId] = useState('');
  const [saveError, setSaveError] = useState('');
  const [expandedSystemIds, setExpandedSystemIds] = useState<string[]>([]);
  const bindings = getAppSystemBindings(definition).flatMap((binding) => {
    const system = systems.find((item) => item.id === binding.systemId);
    return system ? [{ ...binding, system }] : [];
  });
  const [activeSection, setActiveSection] = useState('Data and connectors');

  const changeSystem = async (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    setSavingSystemId(sourceId);
    setSaveError('');
    try {
      await onSystemChange(sourceId, targetId);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Unable to change this system.');
    } finally {
      setSavingSystemId('');
    }
  };

  return (
    <SidebarProvider
      className="h-full min-h-0 w-full overflow-hidden bg-app-surface-muted"
      style={
        {
          '--sidebar-width': '13.5rem',
          '--sidebar': 'var(--app-surface-card)',
          '--sidebar-foreground': 'var(--app-text)',
          '--sidebar-accent': 'var(--app-surface-muted)',
          '--sidebar-accent-foreground': 'var(--app-text)',
          '--sidebar-border': 'var(--app-border)',
          '--sidebar-ring': 'var(--app-brand-strong)',
        } as CSSProperties
      }
    >
      <Sidebar
        collapsible="none"
        className="hidden shrink-0 border-r border-sidebar-border sm:flex"
      >
        <SidebarContent className="px-0 pb-3 pt-4">
          <SidebarMenu>
            {sections.map(({ label, expandable }) => {
              const isActive = activeSection === label;
              return (
                <SidebarMenuItem key={label}>
                  <SidebarMenuButton
                    aria-current={isActive ? 'page' : undefined}
                    isActive={isActive}
                    onClick={() => setActiveSection(label)}
                    className={cn(
                      'h-9.5 rounded-none pl-5 pr-4 font-medium text-app-text-secondary hover:bg-transparent hover:text-app-text-secondary active:bg-transparent',
                      isActive &&
                        'relative font-bold text-app-text before:absolute before:inset-y-0 before:left-0.5 before:w-0.5 before:bg-app-brand-strong data-active:bg-transparent'
                    )}
                  >
                    <span>{label}</span>
                    {expandable && (
                      <ChevronRight
                        className="ml-auto size-3 text-app-text-muted"
                        aria-hidden="true"
                      />
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarContent>
      </Sidebar>
      <ScrollArea id="fabrik-settings-panel" className="min-w-0 flex-1">
        <ScrollAreaViewport className="overscroll-contain">
          <ScrollAreaContent className="w-full min-w-0! px-5 py-5 sm:px-8 sm:py-8">
            <div className="mx-auto max-w-3xl">
              {activeSection === 'Appearance' ? (
                <AppearanceSettings
                  tenantId={tenantId}
                  tenantName={tenantName}
                  appId={appId}
                  definition={definition}
                  version={version}
                  onDefinitionChange={onDefinitionChange}
                  onRenameWorkspace={onRenameApp}
                  canonicalWorkspaceName={appName}
                  appSlug={appSlug}
                  isAppPublished={isAppPublished}
                />
              ) : activeSection === 'Data and connectors' ? (
                <section aria-labelledby="connected-systems-heading">
                  <h2
                    id="connected-systems-heading"
                    className="mb-3 border-b border-app-border pb-2 text-xs font-semibold uppercase tracking-wide text-app-text-muted"
                  >
                    Connected systems
                  </h2>
                  <p className="mb-3 text-sm text-app-text-secondary">
                    {version === 'published'
                      ? 'Review or change the connections used by the live version. Changes save to the live version and do not change the draft.'
                      : 'Review or change the connections used by the draft. Changes save to the draft. Choose the live systems in the Publish dialog.'}
                  </p>
                  {saveError && (
                    <p className="mb-3 text-sm text-app-danger" role="alert">
                      {saveError}
                    </p>
                  )}
                  {bindings.length ? (
                    <div className="flex flex-col gap-3">
                      {bindings.map(
                        ({ system, componentCount, dataSources, usesSharePointExport }) => {
                          const platformSystemUrl = getPlatformSystemURL(tenantId, system.id);
                          const sameTypeSystems = systems.filter(
                            (candidate) => candidate.code === system.code
                          );
                          const connectorLabel = [
                            ...(dataSources.length
                              ? [system.code === 'WORKDAY' ? 'Workday' : 'OData']
                              : []),
                            ...(usesSharePointExport ? ['SharePoint export'] : []),
                          ].join(', ');
                          const isExpanded = expandedSystemIds.includes(system.id);
                          const renderConnectionStatus = (connectedSystem: ConnectedSystem) => {
                            const statusLabel =
                              connectedSystem.pingStatus === 'SUCCESS'
                                ? 'Connected'
                                : connectedSystem.pingStatus === 'FAILED'
                                ? 'Connection failed'
                                : 'Status unknown';
                            return (
                              <span
                                role="img"
                                aria-label={`Connection status: ${statusLabel}`}
                                title={statusLabel}
                                className={cn(
                                  'size-2 shrink-0 rounded-full',
                                  connectedSystem.pingStatus === 'SUCCESS' && 'bg-app-brand-strong',
                                  connectedSystem.pingStatus === 'FAILED' && 'bg-app-danger',
                                  !connectedSystem.pingStatus && 'bg-app-text-muted'
                                )}
                              />
                            );
                          };
                          const renderSystemInfo = (connectedSystem: ConnectedSystem) => (
                            <span className="flex min-w-0 items-center gap-2">
                              {renderConnectionStatus(connectedSystem)}
                              <span className="min-w-0 truncate font-semibold text-app-text">
                                {connectedSystem.name}
                              </span>
                              {connectedSystem.environment && (
                                <Badge variant="secondary">{connectedSystem.environment}</Badge>
                              )}
                              {connectedSystem.companyId && (
                                <span className="hidden truncate text-xs font-normal text-app-text-muted sm:inline">
                                  · Company ID: {connectedSystem.companyId}
                                </span>
                              )}
                            </span>
                          );

                          return (
                            <div
                              key={system.id}
                              className="rounded-xl border border-app-border bg-app-surface-card"
                            >
                              <div className="flex min-w-0 items-center gap-2 px-4 pt-4">
                                <img
                                  src={getConnectedSystemLogoUrl(system.code)}
                                  alt={getConnectedSystemLabel(system.code)}
                                  className="h-5 w-auto max-w-full shrink-0 object-contain"
                                  onError={(event) => {
                                    event.currentTarget.style.display = 'none';
                                  }}
                                />
                                {sameTypeSystems.length > 1 ? (
                                  <Select
                                    value={system.id}
                                    disabled={Boolean(savingSystemId)}
                                    onValueChange={(value) => {
                                      if (value) void changeSystem(system.id, value);
                                    }}
                                  >
                                    <SelectTrigger
                                      aria-label={`System for ${getConnectedSystemLabel(
                                        system.code
                                      )}`}
                                      className="h-8 min-w-0 max-w-full flex-row-reverse"
                                    >
                                      <SelectValue className="min-w-0">
                                        {(value: string | null) => {
                                          const selectedSystem = sameTypeSystems.find(
                                            (candidate) => candidate.id === value
                                          );
                                          return selectedSystem
                                            ? renderSystemInfo(selectedSystem)
                                            : 'Select a system';
                                        }}
                                      </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent
                                      align="start"
                                      alignItemWithTrigger={false}
                                      className="max-h-72"
                                    >
                                      <SelectGroup>
                                        <SelectLabel className="px-2.5 py-2">
                                          Connected {getConnectedSystemLabel(system.code)} systems
                                        </SelectLabel>
                                        {sameTypeSystems.map((candidate) => (
                                          <SelectItem
                                            key={candidate.id}
                                            value={candidate.id}
                                            className="min-h-10 py-2 pl-2.5 pr-8"
                                          >
                                            <span className="flex min-w-0 flex-1 items-center gap-3">
                                              {renderConnectionStatus(candidate)}
                                              <span className="min-w-0 flex-1 truncate">
                                                {candidate.name}
                                              </span>
                                              <Badge variant="secondary">
                                                {candidate.environment || 'Environment not set'}
                                              </Badge>
                                            </span>
                                          </SelectItem>
                                        ))}
                                      </SelectGroup>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  renderSystemInfo(system)
                                )}
                                <button
                                  type="button"
                                  aria-expanded={isExpanded}
                                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${
                                    system.name
                                  }`}
                                  onClick={() =>
                                    setExpandedSystemIds((current) =>
                                      current.includes(system.id)
                                        ? current.filter((id) => id !== system.id)
                                        : [...current, system.id]
                                    )
                                  }
                                  className="ml-auto flex size-7 shrink-0 items-center justify-center rounded-md text-app-text-muted hover:bg-app-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-brand-strong"
                                >
                                  <ChevronDown
                                    className={cn(
                                      'size-4 transition-transform',
                                      isExpanded && 'rotate-180'
                                    )}
                                    aria-hidden="true"
                                  />
                                </button>
                              </div>
                              <div className="flex items-center gap-2 px-4 pb-3 text-xs text-app-text-muted">
                                <span>
                                  Bound by {componentCount} component
                                  {componentCount === 1 ? '' : 's'}
                                </span>
                                {savingSystemId === system.id && <span>Saving system…</span>}
                                <a
                                  href={platformSystemUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  aria-label={`Open ${system.name} in Platform Systems`}
                                  className="ml-auto inline-flex shrink-0 items-center gap-1 font-medium text-app-brand-strong hover:underline"
                                >
                                  Open
                                  <ExternalLink className="size-3.5" aria-hidden="true" />
                                </a>
                              </div>
                              {isExpanded && (
                                <div className="px-4 pb-4">
                                  <div className="mb-3 border-t border-app-border pt-3">
                                    <h4 className="m-0 text-sm font-semibold leading-5 text-app-text">
                                      What this app binds
                                    </h4>
                                  </div>
                                  <div className="flex flex-col gap-1.5">
                                    {dataSources.map((source, index) => (
                                      <div
                                        key={`${source.entity}-${index}`}
                                        className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm leading-5"
                                      >
                                        <span className="font-medium text-app-text">
                                          {source.entity}
                                        </span>
                                        <span className="text-app-text-secondary">
                                          {source.fieldsBound} field
                                          {source.fieldsBound === 1 ? '' : 's'} bound
                                        </span>
                                      </div>
                                    ))}
                                    {usesSharePointExport && (
                                      <p className="m-0 text-sm leading-5 text-app-text-secondary">
                                        <span className="font-medium text-app-text">
                                          Excel export
                                        </span>
                                        {' destination'}
                                      </p>
                                    )}
                                  </div>
                                  <p className="mb-0 mt-3 text-xs text-app-text-muted">
                                    Connector: {connectorLabel}
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  ) : (
                    <p className="m-0 text-sm text-app-text-muted">
                      No connected systems are used by this app.
                    </p>
                  )}
                </section>
              ) : (
                <p className="m-0 text-sm text-app-text-muted">Coming soon.</p>
              )}
            </div>
          </ScrollAreaContent>
        </ScrollAreaViewport>
        <ScrollBar />
      </ScrollArea>
    </SidebarProvider>
  );
}
